/**
 * Scheduled Tasks admin page.
 * Displays background job states with enable/disable toggles, manual run triggers,
 * and paginated run history.
 */

import { useState, useCallback } from "react";
import { Clock, Play, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { GlassCard } from "@/components/sc/glass-card";
import { DataTable, type Column } from "@/components/sc/data-table";
import { EmptyState } from "@/components/sc/empty-state";
import {
  useScheduledTasks,
  useScheduledTaskHistory,
  useAllTaskHistory,
  useToggleScheduledTask,
  useRunScheduledTask,
} from "@/hooks/use-scheduled-tasks";
import type { ScheduledTaskState, ScheduledTaskRun, ScheduledTaskRunStatus } from "@gdapwif/shared";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffMs = now - then;
  const absDiff = Math.abs(diffMs);
  const isFuture = diffMs < 0;

  if (absDiff < 60_000) {
    const secs = Math.round(absDiff / 1000);
    return isFuture ? `in ${secs}s` : `${secs}s ago`;
  }
  if (absDiff < 3_600_000) {
    const mins = Math.round(absDiff / 60_000);
    return isFuture ? `in ${mins} min` : `${mins} min ago`;
  }
  if (absDiff < 86_400_000) {
    const hours = Math.round(absDiff / 3_600_000);
    return isFuture ? `in ${hours}h` : `${hours}h ago`;
  }
  const days = Math.round(absDiff / 86_400_000);
  if (days === 1) return isFuture ? "tomorrow" : "yesterday";
  return isFuture ? `in ${days}d` : `${days}d ago`;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// ---------------------------------------------------------------------------
// Category badge
// ---------------------------------------------------------------------------

const CATEGORY_STYLES: Record<string, string> = {
  gdap: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  pim: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  cache: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  notifications: "bg-sc-success-bg text-sc-success",
  system: "bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400",
};

function CategoryBadge({ category }: { readonly category: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded-full text-badge font-medium",
        CATEGORY_STYLES[category] ?? CATEGORY_STYLES.system,
      )}
    >
      {category}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Status dot
// ---------------------------------------------------------------------------

const STATUS_DOT_COLORS: Record<ScheduledTaskRunStatus, string> = {
  success: "bg-sc-success",
  failed: "bg-sc-danger",
  skipped: "bg-gray-400",
};

function StatusDot({ status }: { readonly status: ScheduledTaskRunStatus }) {
  return (
    <span
      className={cn("inline-block h-2 w-2 rounded-full shrink-0", STATUS_DOT_COLORS[status])}
      title={status}
    />
  );
}

// ---------------------------------------------------------------------------
// Run status badge (for table)
// ---------------------------------------------------------------------------

const RUN_STATUS_STYLES: Record<ScheduledTaskRunStatus, string> = {
  success: "bg-sc-success-bg text-sc-success",
  failed: "bg-red-100 text-sc-danger dark:bg-red-900/30",
  skipped: "bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400",
};

function RunStatusBadge({ status }: { readonly status: ScheduledTaskRunStatus }) {
  return (
    <span
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded-full text-badge font-medium capitalize",
        RUN_STATUS_STYLES[status],
      )}
    >
      {status}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Task card
// ---------------------------------------------------------------------------

interface TaskCardProps {
  readonly task: ScheduledTaskState;
  readonly onToggle: (taskId: string, enabled: boolean) => void;
  readonly onRun: (taskId: string) => void;
  readonly isRunning: boolean;
  readonly isSelected: boolean;
  readonly onSelect: (taskId: string) => void;
}

function TaskCard({ task, onToggle, onRun, isRunning, isSelected, onSelect }: TaskCardProps) {
  const handleRun = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onRun(task.id);
    },
    [onRun, task.id],
  );

  const handleToggle = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      onToggle(task.id, e.target.checked);
    },
    [onToggle, task.id],
  );

  return (
    <div
      className={cn(
        "rounded-card border p-4 transition-colors cursor-pointer",
        isSelected
          ? "border-sc-primary bg-sc-primary/5"
          : "border-sc-border bg-sc-surface-hover/30 hover:bg-sc-surface-hover/50",
      )}
      onClick={() => onSelect(task.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(task.id);
        }
      }}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-body-sm font-medium text-sc-text">{task.definition.displayName}</span>
            <CategoryBadge category={task.definition.category} />
            {task.lastRunStatus && <StatusDot status={task.lastRunStatus} />}
          </div>
          <p className="text-label text-sc-text-muted mt-0.5 truncate">
            {task.definition.description}
          </p>
          <div className="flex items-center gap-4 mt-2 text-label text-sc-text-muted flex-wrap">
            <span>{task.definition.scheduleHuman}</span>
            {task.lastRunAt && (
              <span>
                Last: {formatRelativeTime(task.lastRunAt)}
                {task.lastRunDurationMs !== null && task.lastRunDurationMs !== undefined && ` (${formatDuration(task.lastRunDurationMs)})`}
              </span>
            )}
            {task.nextRunAt && <span>Next: {formatRelativeTime(task.nextRunAt)}</span>}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-4">
          <button
            onClick={handleRun}
            disabled={isRunning}
            className="flex items-center gap-1 px-3 py-1.5 rounded-btn text-body-sm font-medium border border-sc-border text-sc-text hover:bg-sc-surface-hover disabled:opacity-50 transition-colors"
          >
            {isRunning ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            Run Now
          </button>
          {/* Toggle switch */}
          <label
            className="relative inline-flex items-center cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={task.enabled}
              disabled={!task.definition.canDisable}
              onChange={handleToggle}
              className="sr-only peer"
            />
            <div
              className={cn(
                "w-9 h-5 rounded-full transition-colors",
                "after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all",
                "peer-checked:after:translate-x-full",
                task.enabled ? "bg-sc-primary" : "bg-gray-300 dark:bg-gray-600",
                !task.definition.canDisable && "opacity-50 cursor-not-allowed",
              )}
            />
          </label>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// History table columns
// ---------------------------------------------------------------------------

const HISTORY_COLUMNS: readonly Column<ScheduledTaskRun>[] = [
  {
    key: "taskId",
    header: "Task",
    render: (run) => <span className="font-medium">{run.taskId}</span>,
  },
  {
    key: "startedAt",
    header: "Started",
    sortable: true,
    render: (run) => (
      <span title={new Date(run.startedAt).toLocaleString()}>
        {formatRelativeTime(run.startedAt)}
      </span>
    ),
  },
  {
    key: "durationMs",
    header: "Duration",
    render: (run) => formatDuration(run.durationMs),
  },
  {
    key: "status",
    header: "Status",
    render: (run) => <RunStatusBadge status={run.status} />,
  },
  {
    key: "trigger",
    header: "Trigger",
    render: (run) => (
      <span className="capitalize text-sc-text-muted">{run.trigger}</span>
    ),
  },
  {
    key: "result",
    header: "Result",
    className: "max-w-[200px]",
    render: (run) => {
      if (run.errorMessage) {
        return <span className="text-sc-danger truncate block">{run.errorMessage}</span>;
      }
      if (run.result) {
        const summary = JSON.stringify(run.result);
        return (
          <span className="text-sc-text-muted truncate block" title={summary}>
            {summary.length > 60 ? `${summary.slice(0, 60)}...` : summary}
          </span>
        );
      }
      return <span className="text-sc-text-muted">--</span>;
    },
  },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ScheduledTasksPage() {
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(10);
  const [runningTaskId, setRunningTaskId] = useState<string | null>(null);

  const tasksQuery = useScheduledTasks();
  const allHistoryQuery = useAllTaskHistory(historyPage, historyPageSize);
  const taskHistoryQuery = useScheduledTaskHistory(
    selectedTaskId,
    historyPage,
    historyPageSize,
  );
  const toggleMutation = useToggleScheduledTask();
  const runMutation = useRunScheduledTask();

  // Use filtered history when a task is selected, otherwise all history
  const historyQuery = selectedTaskId ? taskHistoryQuery : allHistoryQuery;
  const historyData = historyQuery.data?.items ?? [];
  const historyTotal = historyQuery.data?.total ?? 0;

  const handleToggle = useCallback(
    (taskId: string, enabled: boolean) => {
      toggleMutation.mutate(
        { taskId, enabled },
        {
          onSuccess: () => {
            toast.success(`Task ${enabled ? "enabled" : "disabled"}`);
          },
          onError: () => {
            toast.error("Failed to update task. Try again.");
          },
        },
      );
    },
    [toggleMutation],
  );

  const handleRun = useCallback(
    (taskId: string) => {
      setRunningTaskId(taskId);
      runMutation.mutate(taskId, {
        onSuccess: () => {
          toast.success("Task triggered successfully");
          setRunningTaskId(null);
        },
        onError: () => {
          toast.error("Failed to trigger task. Try again.");
          setRunningTaskId(null);
        },
      });
    },
    [runMutation],
  );

  const handleSelectTask = useCallback(
    (taskId: string) => {
      setSelectedTaskId((prev) => (prev === taskId ? null : taskId));
      setHistoryPage(1);
    },
    [],
  );

  if (tasksQuery.isLoading) {
    return (
      <GlassCard className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="mx-auto mb-2 h-6 w-6 rounded-full bg-sc-primary animate-sc-pulse" />
          <p className="text-body-sm text-sc-text-muted">Loading scheduled tasks...</p>
        </div>
      </GlassCard>
    );
  }

  const tasks = tasksQuery.data ?? [];

  return (
    <div className="space-y-6">
      {/* Task cards */}
      <GlassCard>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock size={18} className="text-sc-primary" />
            <h3 className="text-h3 font-display text-sc-navy">Scheduled Tasks</h3>
          </div>
          <button
            onClick={() => void tasksQuery.refetch()}
            className="flex items-center gap-1 px-2 py-1 rounded-btn text-body-sm text-sc-text-muted hover:text-sc-text hover:bg-sc-surface-hover transition-colors"
            title="Refresh"
          >
            <RefreshCw size={14} className={tasksQuery.isFetching ? "animate-spin" : ""} />
          </button>
        </div>
        {tasks.length === 0 ? (
          <p className="text-body-sm text-sc-text-muted py-8 text-center">
            No scheduled tasks configured.
          </p>
        ) : (
          <div className="space-y-3">
            {tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                onToggle={handleToggle}
                onRun={handleRun}
                isRunning={runningTaskId === task.id}
                isSelected={selectedTaskId === task.id}
                onSelect={handleSelectTask}
              />
            ))}
          </div>
        )}
      </GlassCard>

      {/* Run history */}
      <GlassCard>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-h3 font-display text-sc-navy">Run History</h3>
          {selectedTaskId && (
            <button
              onClick={() => {
                setSelectedTaskId(null);
                setHistoryPage(1);
              }}
              className="text-body-sm text-sc-primary hover:underline"
            >
              Clear filter: {selectedTaskId}
            </button>
          )}
        </div>
        <DataTable<ScheduledTaskRun>
          columns={HISTORY_COLUMNS}
          data={historyData}
          total={historyTotal}
          page={historyPage}
          pageSize={historyPageSize}
          onPageChange={setHistoryPage}
          onPageSizeChange={(size) => {
            setHistoryPageSize(size);
            setHistoryPage(1);
          }}
          rowKey={(run) => String(run.id)}
          isLoading={historyQuery.isLoading}
          emptyState={
            <EmptyState
              icon={<Clock size={40} />}
              heading="No run history"
              description={
                selectedTaskId
                  ? "This task has no recorded runs yet."
                  : "No task runs have been recorded yet."
              }
            />
          }
        />
      </GlassCard>
    </div>
  );
}
